var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import strings from 'strings.json';
import get from 'lodash/get';
import { template } from 'utils/stringUtils';
const TEMPLATE_PATTERN = /{@\S+?}/g;
const REPLACE_PATTERN = /{@(\S+)}/;
const DEV = process.env.NODE_ENV === 'development';
const TEST = process.env.NODE_ENV === 'test';
// Returns an array of interpolated sections. If string is "this is a
// {@reactElement}", the interpolated sections would look something like:
//
// ['this is a ', actualReactElementWePass ]
export function interpolateString(str, attributes, props) {
    // TODO: We should strip all the items in the array that are empty
    // TODO: Try out using while(templatestring = regex.exec(str)) style loop
    // here.
    const splitString = str.split(TEMPLATE_PATTERN);
    const templates = str.match(TEMPLATE_PATTERN) || [];
    const stack = [splitString[0]];
    // Start with the second value in the splitString array. For each of the
    // template values, merge all the consecutive non-React elements into a single
    // entity, without changing the order of elements. That is, the following
    // array:
    //
    // ['this', ' is', ReactElement1, ' a', ' string]
    //
    // will be reduced to:
    //
    // [ 'this is', ReactElement1, ' a string' ]
    const splitLength = splitString.length;
    for (let idx = 1; idx < splitLength; idx++) {
        const str = splitString[idx];
        const templateName = templates[idx - 1] ? templates[idx - 1].replace(REPLACE_PATTERN, '$1') : null;
        // TODO: We are assuming the return value below will be a primitive. For non TS code, this might potentially be a problem.
        let templateValue = get(attributes, [templateName]);
        // Normalize value if input is falsy value except 0
        if (!templateValue && templateValue !== 0) {
            templateValue = '';
        }
        // templateValue can be a string or some primitive value, or a
        // react element.
        if (React.isValidElement(templateValue)) {
            stack.push(templateValue);
            stack.push(str);
        }
        else {
            // Adding this normalization step here because we first need to
            // check if the value is not a react element.
            if (typeof templateValue === 'object') {
                templateValue = '';
            }
            // We always have a string right after a react element because we push the
            // string at the end in previous iteration.
            const arrIdx = stack.length - 1;
            stack[arrIdx] = `${stack[arrIdx]}${templateValue}${str}`;
        }
    }
    // This routine wraps react elements inside a span that has a `key` attribute
    // defined. if the stack size is greater than 1 to ensure we are doing this
    // only when the array is not a single-string value.
    if (stack.length > 1) {
        let key = 0;
        const stackLength = stack.length;
        for (let idx = 0; idx < stackLength; idx++) {
            const elem = stack[idx];
            if (React.isValidElement(elem)) {
                if ((DEV || TEST) && elem.key && props) {
                    const { strKey } = props;
                    // tslint:disable-next-line no-console
                    console.error(`prop "key" passed to StringWidget will get overridden. Check component with strKey ${strKey}`);
                }
                const newElem = React.cloneElement(elem, { key: key++ });
                stack[idx] = newElem;
            }
        }
    }
    return stack;
}
// TODO: Add checks for template variables for a given string and corresponding
// `attributes` in development mode. These checks ideally should get removed in
// production code.
export default class StringWidget extends React.Component {
    render() {
        const _a = this.props, { strKey, className, contentStyle, attributes } = _a, rest = __rest(_a, ["strKey", "className", "contentStyle", "attributes"]);
        // Return early if the given key is invalid, or if there is no value in
        // strings DB for the given key
        if (typeof strKey === 'undefined' || !strKey)
            return null;
        const str = strings[strKey];
        if (!str)
            return null;
        // fast path when there are no template strings found.
        const children = TEMPLATE_PATTERN.test(str) ? interpolateString(str, attributes) : str;
        return (<span className={className} style={contentStyle} {...rest}>
				{children}
			</span>);
    }
}
// TODO: Add SCU
StringWidget.defaultProps = {
    strKey: ''
};
// Function to check if a key is available with String Widget Component or not
export function isStringTemplateKey(key) {
    return !!strings[key];
}
export function stringTemplate(key, props) {
    return template(strings[key] || '', props);
}
